import { useReducedMotion } from "motion/react";
import { useTranslation } from "react-i18next";

import type { SprintMission } from "@/campaigns/modules/sprint-mission/types";
import {
  finishedAt,
  prizesRemaining,
} from "@/campaigns/modules/sprint-mission/types";
import { AnimatedNumber } from "@/components/ui/animated-number";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { useSprintMissionPhase } from "./use-sprint-mission-phase";

/** "12 Sep, 14:32" in the player's locale — null if the date does not parse. */
const formatFinishedAt = (d: Date | null): string | null =>
  d
    ? new Intl.DateTimeFormat(undefined, {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }).format(d)
    : null;

/**
 * The winners card — how many players can still win, and how fast that is shrinking.
 *
 * This is the campaign type's whole proposition, so it leads the main column: a large tabular
 * number, the word "Winners left", how many have already won, a LIVE chip, and a bar showing
 * what is left of the pool. It is deliberately about the *pool*, not the player — the player's
 * own progress lives in the mission card underneath.
 *
 * Colour follows the phase (`sprintMissionPhase`): calm and won are the success green, crucial is
 * the urgent orange with a glow on the card, finished is muted with the count showing the pool
 * total. Motion is confined to the digit swap, and only when the player has not asked for less
 * of it.
 */
export const SprintMissionWinnersCard = ({
  sprintMission,
  className,
}: {
  sprintMission: SprintMission;
  className?: string;
}) => {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const phase = useSprintMissionPhase(sprintMission);
  const remaining = prizesRemaining(sprintMission);

  // `loading`: the pool stream has not pushed yet. Rendering nothing beats rendering "0", which
  // would tell the player the opposite of the truth.
  if (phase === "loading" || remaining === null) return null;

  // `pool` is non-null here: `remaining` is derived from it and the guard above returns on
  // null, so a `?? 0` fallback would be dead code that reads as a real case (!4231).
  const total = sprintMission.pool!.prize_pool_size;
  const finished = phase === "finished";
  const crucial = phase === "crucial";
  const ratio = total > 0 ? Math.min(1, Math.max(0, remaining / total)) : 0;
  const finishedDate = finished
    ? formatFinishedAt(finishedAt(sprintMission))
    : null;

  // Finished shows the pool as a whole ("50 Winners"); every live phase shows what is left.
  const count = finished ? total : remaining;
  // `font-heading` is explicit: the count sits in a body-font context and Geist has no 800
  // weight loaded, so without it the hero number rendered as a synthesized-bold Geist.
  const countClass = cn(
    "font-heading text-[52px] leading-none font-extrabold tabular-nums ls:text-[40px]",
    finished && "text-muted-foreground",
    crucial && "text-urgent",
    !finished && !crucial && "text-active"
  );

  return (
    <Card
      // Urgency lights the fill and rim in `--urgent` — the same treatment as the jackpot pot
      // in its final minute.
      urgent={crucial}
      data-phase={phase}
      className={cn("relative flex flex-col gap-4", className)}
    >
      {!finished && (
        // The chip sits in the corner, above the count row, so it never competes with it.
        <span className="absolute top-4 right-4 flex items-center gap-1.5 font-heading text-[10px] leading-3 font-semibold tracking-[0.8px] text-muted-foreground uppercase">
          <span
            aria-hidden
            className={cn(
              "size-1.5 rounded-full",
              crucial ? "bg-urgent" : "bg-active"
            )}
          />
          {t("sprintMission.live", "Live")}
        </span>
      )}

      <div className="flex w-full flex-wrap items-end justify-between gap-x-4 gap-y-1">
        {/* The count moves under the player without any action of theirs, so it is announced
            rather than silently swapped. `aria-live="polite"` waits for a pause in speech —
            `assertive` would interrupt a screen-reader user mid-sentence for a number that is
            informational, not an alert. */}
        {/* `role="heading"` rather than an `<h3>`: `AnimatedNumber` renders a `div`, and a `div`
            inside a heading is invalid HTML. The ARIA role gives the same outline entry, which a
            screen-reader user navigating by heading needs to find the one number this type
            exists to show — `aria-live` alone only announces *changes*. */}
        <div
          role="heading"
          aria-level={3}
          aria-live="polite"
          className="flex items-baseline gap-3"
        >
          {reduceMotion || finished ? (
            <span className={countClass}>{count}</span>
          ) : (
            <AnimatedNumber value={count} className={countClass} />
          )}
          <span
            className={cn(
              "font-heading text-lg leading-6 font-bold ls:text-[15px] ls:leading-5",
              finished ? "text-muted-foreground" : "text-foreground"
            )}
          >
            {/* Pluralised on the number beside it: "1 Winner left", "2 Winners left". */}
            {finished
              ? t("sprintMission.winners", "Winners", { count })
              : t("sprintMission.winnersLeft", "Winners left", { count })}
          </span>
        </div>

        {/* Live: the pool size as a caption. Finished: the end date when it is derivable, else
            nothing — the count already reads "50 Winners". */}
        {(!finished || finishedDate) && (
          <span className="font-body text-xs leading-4 text-muted-foreground tabular-nums">
            {finished
              ? t("sprintMission.finishedOn", "Finished {{date}}", {
                  date: finishedDate,
                })
              : t("sprintMission.maxWinners", "Max {{total}} winners", {
                  total,
                })}
          </span>
        )}
      </div>

      {/* Remaining share of the pool. Decorative: the numbers above carry the meaning. */}
      {/* The shared progress bar (mission tasks, prize-drop tiers): active while slots are open,
          urgent in the final stretch, grey once the pool is closed. */}
      <div
        aria-hidden
        className={cn(
          "task-progress-track w-full",
          finished && "progress-done",
          crucial && "progress-urgent"
        )}
      >
        <div
          className="task-progress-fill"
          style={{
            width: finished ? "100%" : `${Math.round(ratio * 100)}%`,
            // The live bar reads left-to-right as a drain: solid at its end, lighter behind it.
            backgroundImage: finished
              ? undefined
              : `linear-gradient(to right, color-mix(in srgb, var(${
                  crucial ? "--urgent" : "--active"
                }) 70%, transparent), var(${crucial ? "--urgent" : "--active"}))`,
          }}
        />
      </div>
    </Card>
  );
};
